import {
  AlertIcon,
  BellIcon,
  BugIcon,
  CheckIcon,
  CircleSlashIcon,
  ClockIcon,
  CommentIcon,
  DatabaseIcon,
  GitCommitIcon,
  GitMergeIcon,
  GitPullRequestIcon,
  HistoryIcon,
  IssueReopenedIcon,
  KeyIcon,
  LockIcon,
  MirrorIcon,
  PencilIcon,
  PeopleIcon,
  PlayIcon,
  RepoIcon,
  RepoPullIcon,
  RepoPushIcon,
  ServerIcon,
  ShieldCheckIcon,
  ShieldIcon,
  SyncIcon,
  UnlockIcon,
  UploadIcon,
  VerifiedIcon,
  XIcon,
} from "@primer/octicons-react";
import type { ReactNode } from "react";
import type { AuditEntry } from "../api/types";
import { Sha } from "./common";
import { detailText } from "./Details";
import { forge } from "./forge";
import { bytes, short } from "./format";
import { Badge, type Tone } from "./ui/badge";

export interface AuditEvent {
  Icon: typeof CheckIcon;
  tone: Tone;
  /** Predicate of "<actor> …", e.g. "landed on main". */
  text: ReactNode;
}

/** Audit filters shown as chips; each matches entries whose action starts with any prefix. `org` ones also apply to an organization's trail, not a single repository's. */
export const AUDIT_CATEGORIES: Array<{ id: string; label: string; prefixes: string[]; org?: boolean }> = [
  { id: "changes", label: "Changes", prefixes: ["change."] },
  { id: "main", label: "Main branch", prefixes: ["ref.", "main.", "repo.init"] },
  { id: "policy", label: "Policy", prefixes: ["policy.", "repo.freeze", "repo.unfreeze"] },
  { id: "access", label: "Access", prefixes: ["repo.access"] },
  { id: "ci", label: "CI & verification", prefixes: ["ci.", "verify.", "notify.", "alert."] },
  { id: "storage", label: "Storage", prefixes: ["objects.", "repo.compact", "repo.rebuild_check"] },
  { id: "tokens", label: "Tokens", prefixes: ["token."], org: true },
  { id: "repos", label: "Repositories", prefixes: ["repo.create", "repo.update", "repo.import"], org: true },
  { id: "mirror", label: "Remote sync", prefixes: ["mirror.", "remote.failed"], org: true },
  { id: "runners", label: "Runners", prefixes: ["runner."], org: true },
  { id: "remote", label: "Remote", prefixes: ["remote."] },
];

const RUNNER_PLATFORMS: Record<string, string> = { kind: "local kind", gcp: "Google Cloud", aws: "AWS", cloudflare: "Cloudflare", ubicloud: "Ubicloud" };
const list = (xs: unknown): string => (Array.isArray(xs) && xs.length ? xs.join(", ") : "");
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const Code = ({ children }: { children: ReactNode }) => <span className="font-mono">{children}</span>;

function Commit({ repo, sha }: { repo?: string; sha: unknown }) {
  if (typeof sha !== "string" || !sha) return null;
  return repo ? <Sha repo={repo} sha={sha} n={7} /> : <span className="font-mono text-xs text-muted-foreground">{short(sha, 7)}</span>;
}

/** reason is a change's Reason ({code, message, …}) or a plain string. */
function reasonText(reason: any): string {
  if (!reason) return "";
  if (typeof reason === "string") return reason;
  return reason.message ?? reason.code ?? "";
}

/** A human sentence for an audit entry. repo links shas; global entries carry their repo in detail. */
export function describeAudit(a: AuditEntry, repo?: string): AuditEvent {
  const d = a.detail ?? {};
  const r = repo || d.repo || undefined;
  switch (a.action) {
    case "change.created":
      if (!d.commit) return { tone: "green", Icon: IssueReopenedIcon, text: "resolved conflicts and resubmitted the change" };
      return {
        tone: "green",
        Icon: GitCommitIcon,
        text: (
          <>
            created the change <Commit repo={r} sha={d.commit} />
            <span className="text-faint">
              {plural(d.paths ?? 0, "file")} · {d.risk_tier ?? "normal"} risk{d.task_id ? ` · task ${d.task_id}` : ""}
            </span>
          </>
        ),
      };
    case "change.ci":
      return d.rebased_onto
        ? {
            tone: "orange",
            Icon: PlayIcon,
            text: (
              <>
                rebased onto <Commit repo={r} sha={d.rebased_onto} /> · rerunning CI on the commit that lands
              </>
            ),
          }
        : { tone: "orange", Icon: PlayIcon, text: "requested landing · running CI" };
    case "change.ci_tier1":
      return { tone: "orange", Icon: PlayIcon, text: "requested landing · running CI" };
    case "change.ci_tier2":
      return { tone: "orange", Icon: PlayIcon, text: "running CI" };
    case "change.ready":
      return d.requeued
        ? { tone: "blue", Icon: SyncIcon, text: `re-queued to land (${d.requeued})` }
        : { tone: "blue", Icon: CheckIcon, text: "all required checks passed · queued to land" };
    case "change.landing":
      return { tone: "blue", Icon: GitMergeIcon, text: "landing (rebase + CAS on main)" };
    case "change.train":
      return {
        tone: "blue",
        Icon: SyncIcon,
        text: (
          <>
            joined the merge train as <Commit repo={r} sha={d.commit} />
            <span className="text-faint">
              {d.car ? `behind ${plural(d.car, "change")}` : "at the front"}
              {list(d.unioned) ? ` · kept both sides in ${list(d.unioned)}` : ""}
            </span>
          </>
        ),
      };
    case "change.retarget":
      return {
        tone: "orange",
        Icon: SyncIcon,
        text: (
          <>
            {d.why ?? "retargeted"} · rebased onto <Commit repo={r} sha={d.onto} /> to rerun CI
          </>
        ),
      };
    case "change.main_red":
      return {
        tone: "orange",
        Icon: ClockIcon,
        text: (
          <>
            CI failed on <Code>{list(d.jobs)}</Code>, but main <Commit repo={r} sha={d.base} /> fails them too · waiting for main to be fixed
          </>
        ),
      };
    case "ci.flaky_retry":
      return { tone: "orange", Icon: SyncIcon, text: <>retried known-flaky <Code>{list(d.jobs)}</Code></> };
    case "change.landed":
      return {
        tone: "purple",
        Icon: GitMergeIcon,
        text: (
          <>
            landed on main{d.rebased ? " (rebased)" : ""} as <Commit repo={r} sha={d.commit} />
          </>
        ),
      };
    case "change.verified":
      return { tone: "purple", Icon: VerifiedIcon, text: "verified the change on main after landing" };
    case "change.reverted":
      return { tone: "red", Icon: HistoryIcon, text: `reverted the change on main${d.reason?.reverted?.reason ? `: ${d.reason.reverted.reason}` : ""}` };
    case "change.rejected":
      return { tone: "red", Icon: XIcon, text: <Rejected reason={d.reason} /> };
    case "change.conflict":
      return { tone: "orange", Icon: AlertIcon, text: `found conflicts with main${reasonText(d.reason) ? `: ${reasonText(d.reason)}` : ""}` };
    case "change.cancelled":
      return { tone: "default", Icon: CircleSlashIcon, text: "cancelled the change" };

    case "policy.deny":
      return { tone: "red", Icon: ShieldIcon, text: <Denied d={d} /> };
    case "repo.freeze":
      return {
        tone: "orange",
        Icon: LockIcon,
        text: list(d.paths) ? (
          <>
            froze paths <Code>{list(d.paths)}</Code>
          </>
        ) : (
          "froze the repository"
        ),
      };
    case "repo.unfreeze":
      return {
        tone: "green",
        Icon: UnlockIcon,
        text: list(d.paths) ? (
          <>
            unfroze paths <Code>{list(d.paths)}</Code>
          </>
        ) : (
          "unfroze the repository"
        ),
      };

    case "ref.land":
    case "ref.revert":
    case "ref.import":
    case "ref.ref_set": {
      const verb = { "ref.land": "advanced", "ref.revert": "reverted", "ref.import": "imported", "ref.ref_set": "set" }[a.action];
      return {
        tone: a.action === "ref.revert" ? "red" : a.action === "ref.ref_set" ? "orange" : "purple",
        Icon: a.action === "ref.revert" ? HistoryIcon : GitCommitIcon,
        text: (
          <>
            {verb} <Code>{d.ref ?? "main"}</Code>
            {d.old ? (
              <>
                {" "}
                <Commit repo={r} sha={d.old} /> →
              </>
            ) : (
              " to"
            )}{" "}
            <Commit repo={r} sha={d.new} />
            {d.subject && <span className="text-faint">“{d.subject}”</span>}
          </>
        ),
      };
    }
    case "main.revert":
      return {
        tone: "red",
        Icon: HistoryIcon,
        text: (
          <>
            reverted <Commit repo={r} sha={d.reverted} /> on main as <Commit repo={r} sha={d.commit} />
            {d.reason && <span className="text-faint">— {d.reason}</span>}
          </>
        ),
      };
    case "repo.init":
      return { tone: "green", Icon: RepoIcon, text: "initialized the repository" };

    case "verify.start":
      return {
        tone: "orange",
        Icon: ShieldCheckIcon,
        text: (
          <>
            started verification #{d.verification} of log entries {d.from_seq + 1}–{d.to_seq} at <Commit repo={r} sha={d.head} />
            {list(d.jobs) && <span className="text-faint">jobs: {list(d.jobs)}</span>}
          </>
        ),
      };
    case "verify.passed":
      return {
        tone: "green",
        Icon: VerifiedIcon,
        text: (
          <>
            verification #{d.verification} passed at <Commit repo={r} sha={d.head} />
          </>
        ),
      };
    case "verify.failed":
      return {
        tone: "red",
        Icon: XIcon,
        text: (
          <>
            verification #{d.verification} failed at <Commit repo={r} sha={d.head} /> ({list(d.failing) || "unknown jobs"}) · bisecting for the culprit
          </>
        ),
      };
    case "notify.agent":
      return { tone: "red", Icon: HistoryIcon, text: "notified the author: the change was reverted after post-land verification failed" };
    case "alert.human":
      return {
        tone: "red",
        Icon: BellIcon,
        text: (
          <>
            paged a human{d.verification ? ` (verification #${d.verification})` : ""}: {d.why}
            {list(d.frozen_paths) && (
              <span className="text-faint">
                froze <Code>{list(d.frozen_paths)}</Code>
              </span>
            )}
          </>
        ),
      };
    case "ci.flaky_flagged":
      return {
        tone: "orange",
        Icon: BugIcon,
        text: (
          <>
            flagged job <Code>{d.job}</Code> as flaky{d.note ? `: ${d.note}` : ""}
          </>
        ),
      };
    case "ci.quarantine":
      return {
        tone: "orange",
        Icon: LockIcon,
        text: (
          <>
            quarantined job <Code>{d.job}</Code> (failures no longer block landing)
          </>
        ),
      };
    case "ci.unquarantine":
      return {
        tone: "green",
        Icon: UnlockIcon,
        text: (
          <>
            released job <Code>{d.job}</Code> from quarantine
          </>
        ),
      };

    case "objects.upload":
      return { tone: "default", Icon: UploadIcon, text: `uploaded ${plural(d.objects ?? 0, "object")} (${bytes(d.bytes ?? 0)})` };
    case "repo.compact":
      return {
        tone: "default",
        Icon: DatabaseIcon,
        text: `compacted storage to generation ${d.generation}: ${d.bundles_in} → ${d.bundles_out} bundles, ${plural(d.objects ?? 0, "object")} kept, ${d.dropped ?? 0} dropped`,
      };
    case "repo.rebuild_check":
      return d.ok
        ? { tone: "green", Icon: DatabaseIcon, text: `rebuild check passed: main replays from ${plural(d.log_entries ?? 0, "log entry")}` }
        : {
            tone: "red",
            Icon: AlertIcon,
            text: (
              <>
                rebuild check FAILED: live main <Commit repo={r} sha={d.live_main} /> ≠ rebuilt <Commit repo={r} sha={d.rebuilt_main} />
              </>
            ),
          };

    case "repo.create":
      return {
        tone: "green",
        Icon: RepoIcon,
        text: (
          <>
            created repository <b>{d.name}</b>
            {d.import && (
              <>
                {" "}
                from GitHub <Code>{d.import.url}</Code>
              </>
            )}
          </>
        ),
      };
    case "repo.import_retry":
      return {
        tone: "default",
        Icon: SyncIcon,
        text: (
          <>
            retried the import of <b>{d.name}</b> from <Code>{d.url}</Code>
          </>
        ),
      };
    case "repo.import_failed":
      return {
        tone: "red",
        Icon: AlertIcon,
        text: (
          <>
            failed to import <b>{d.name}</b>
            {d.detail ? `: ${d.detail}` : ""}
          </>
        ),
      };
    case "repo.update": {
      const what = [
        d.description !== undefined && "description",
        d.mirror_url !== undefined && (d.mirror_url ? `mirror → ${d.mirror_url}` : "cleared mirror"),
      ].filter(Boolean);
      return {
        tone: "default",
        Icon: PencilIcon,
        text: (
          <>
            updated repository <b>{d.name}</b>
            {what.length ? `: ${what.join(", ")}` : ""}
          </>
        ),
      };
    }
    case "runner.placement":
      return d.platform
        ? {
            tone: "blue",
            Icon: ServerIcon,
            text: (
              <>
                saved the <b>{RUNNER_PLATFORMS[d.platform] ?? d.platform}</b> runner placement
                {d.strict ? " (enforced)" : ""}
                {d.replicas ? `: ${plural(d.replicas, "runner")} × ${plural(d.concurrency ?? 1, "job")}` : ""}
              </>
            ),
          }
        : { tone: "default", Icon: ServerIcon, text: "cleared the runner placement: any runner may take CI jobs" };
    case "runner.placement_remove":
      return {
        tone: "default",
        Icon: ServerIcon,
        text: (
          <>
            removed the <b>{RUNNER_PLATFORMS[d.platform] ?? d.platform}</b> runner placement
          </>
        ),
      };
    case "runner.placement_enforce":
      return {
        tone: d.strict ? "blue" : "default",
        Icon: ServerIcon,
        text: d.strict ? (
          <>
            gave CI jobs only to runners on <b>{(d.platforms ?? []).map((p: string) => RUNNER_PLATFORMS[p] ?? p).join(", ")}</b>
          </>
        ) : (
          "stopped enforcing runner placements: any runner may take CI jobs"
        ),
      };
    case "runner.create":
      return {
        tone: "green",
        Icon: ServerIcon,
        text: (
          <>
            registered runner <b>{d.runner}</b>
            {d.platform ? ` for ${RUNNER_PLATFORMS[d.platform] ?? d.platform}` : ""}
            {list(d.repos) ? ` on ${list(d.repos)}` : ""}
            {d.expires ? `; its token expires ${new Date(d.expires).toLocaleDateString()}` : "; its token never expires"}
          </>
        ),
      };
    case "runner.update": {
      const what = [
        d.paused === true && "paused it",
        d.paused === false && "resumed it",
        d.platform !== undefined && (d.platform ? `platform → ${RUNNER_PLATFORMS[d.platform] ?? d.platform}` : "any platform"),
        d.repos !== undefined && (list(d.repos) ? `repositories → ${list(d.repos)}` : "all repositories"),
        d.description !== undefined && "description",
      ].filter(Boolean);
      return {
        tone: d.paused === true ? "yellow" : "default",
        Icon: d.paused === true ? CircleSlashIcon : PencilIcon,
        text: (
          <>
            updated runner <b>{d.runner}</b>
            {what.length ? `: ${what.join(", ")}` : ""}
          </>
        ),
      };
    }
    case "runner.token_reset":
      return {
        tone: "orange",
        Icon: KeyIcon,
        text: (
          <>
            reset the token of runner <b>{d.runner}</b>; the old token no longer works
          </>
        ),
      };
    case "runner.delete":
      return {
        tone: "red",
        Icon: XIcon,
        text: (
          <>
            deleted runner <b>{d.runner}</b> and revoked its token
          </>
        ),
      };
    case "token.issue":
      return { tone: "default", Icon: KeyIcon, text: <Token d={d} /> };
    case "group.create":
      return { tone: "green", Icon: PeopleIcon, text: <>created group <b>{d.group}</b></> };
    case "group.delete":
      return { tone: "red", Icon: PeopleIcon, text: <>deleted group <b>{d.group}</b></> };
    case "group.member_add":
      return { tone: "default", Icon: PeopleIcon, text: <>added <b>{d.user}</b> to group <b>{d.group}</b></> };
    case "group.member_remove":
      return { tone: "default", Icon: PeopleIcon, text: <>removed <b>{d.user}</b> from group <b>{d.group}</b></> };
    case "repo.access_set":
      return {
        tone: "default",
        Icon: PeopleIcon,
        text: (
          <>
            gave {d.group ? "group " : ""}
            <b>{d.group ?? d.user}</b> the <b>{d.role}</b> role
          </>
        ),
      };
    case "repo.access_remove":
      return {
        tone: "default",
        Icon: PeopleIcon,
        text: (
          <>
            removed the access of {d.group ? "group " : ""}
            <b>{d.group ?? d.user}</b>
          </>
        ),
      };
    case "mirror.pushed":
      return {
        tone: "green",
        Icon: MirrorIcon,
        text: (
          <>
            pushed <b>{d.repo}</b> main at <Commit repo={d.repo} sha={d.sha} /> to its remote
          </>
        ),
      };
    case "remote.failed":
      return {
        tone: "red",
        Icon: AlertIcon,
        text: (
          <>
            a remote step failed for <b>{d.repo}</b>
            {d.detail ? `: ${d.detail}` : ""}
          </>
        ),
      };
    case "mirror.failed":
      return {
        tone: "red",
        Icon: MirrorIcon,
        text: (
          <>
            failed to push <b>{d.repo}</b> main to its remote
            {d.detail ? `: ${d.detail}` : ""}
          </>
        ),
      };
    case "remote.add":
      return {
        tone: "blue",
        Icon: RepoPushIcon,
        text: (
          <>
            {d.import ? "imported from" : "added remote"} <Code>{d.url}</Code> ({d.branch}); landed work ships as batch {forge(d.provider).review}s
          </>
        ),
      };
    case "remote.remove":
      return { tone: "default", Icon: RepoPushIcon, text: <>removed remote {d.url ? <Code>{d.url}</Code> : null}</> };
    case "remote.batch_cut":
      return { tone: "default", Icon: GitPullRequestIcon, text: <>cut batch #{d.n} with {plural(d.lands ?? 0, "land")} up to <Commit repo={r} sha={d.head} /></> };
    case "remote.batch_open":
      return { tone: "green", Icon: GitPullRequestIcon, text: <>opened {forge(d.provider).review} {forge(d.provider).ref(d.pr)} for batch #{d.n}</> };
    case "remote.batch_merged":
      return { tone: "purple", Icon: GitMergeIcon, text: <>merged batch #{d.n} ({forge(d.provider).short} {forge(d.provider).ref(d.pr)}) on {forge(d.provider).name}</> };
    case "remote.batch_closed":
      return { tone: "red", Icon: XIcon, text: <>batch #{d.n} ({forge(d.provider).short} {forge(d.provider).ref(d.pr)}) was closed without merging</> };
    case "remote.pull":
      return {
        tone: "green",
        Icon: RepoPullIcon,
        text: (
          <>
            pulled from remote: {d.merge ? "merged" : "fast-forwarded"} main to {forge(d.provider).name}'s <Commit repo={r} sha={d.remote} />
            {d.subject ? `: ${d.subject}` : ""}
          </>
        ),
      };
    case "remote.error":
      return {
        tone: "red",
        Icon: AlertIcon,
        text: <>{d.code === "conflict" ? `${forge(d.provider).name} and main conflict` : "remote push/pull failed"}{d.message ? `: ${d.message}` : ""}</>,
      };
    case "remote.recovered":
      return { tone: "green", Icon: CheckIcon, text: "remote recovered" };
    case "check.holds":
    case "check.broken":
    case "check.pending":
      return {
        tone: a.action === "check.holds" ? "green" : a.action === "check.broken" ? "red" : "yellow",
        Icon: a.action === "check.holds" ? ShieldCheckIcon : a.action === "check.broken" ? XIcon : ClockIcon,
        text: (
          <>
            check <Code>{d.check}</Code> {d.from ? `went from ${d.from} to ` : "is "}
            {d.state} at <Commit repo={r} sha={d.commit} />
            {d.reason ? `: ${d.reason}` : ""}
          </>
        ),
      };
  }
  const rest = detailText({ ...d, change_id: undefined });
  return {
    tone: "default",
    Icon: CommentIcon,
    text: (
      <>
        <Code>{a.action}</Code>
        {rest && <span className="text-faint">{rest}</span>}
      </>
    ),
  };
}

function Rejected({ reason }: { reason: any }) {
  const failed: string[] = reason?.failed?.map((f: any) => f.job) ?? [];
  return (
    <>
      rejected the change{reason?.code && <Badge tone="red">{reason.code}</Badge>}
      {reasonText(reason) && <span>{reasonText(reason)}</span>}
      {failed.length > 0 && !reasonText(reason).includes(failed[0]) && <span className="text-faint">failed: {failed.join(", ")}</span>}
    </>
  );
}

function Denied({ d }: { d: any }) {
  const code = d.reason;
  const why =
    code === "PROTECTED_PATH" ? (
      <>
        touches protected paths <Code>{list(d.paths)}</Code>
      </>
    ) : code === "RATE_LIMITED" ? (
      `already created ${plural(d.hour ?? 0, "change")} in the last hour`
    ) : (
      d.message
    );
  return (
    <>
      was denied by policy<Badge tone="red">{code}</Badge>
      {why && <span>{why}</span>}
    </>
  );
}

function Token({ d }: { d: any }) {
  const scope = [list(d.repos) ? `repos ${list(d.repos)}` : "all repos", list(d.paths) && `paths ${list(d.paths)}`, d.task && `task ${d.task}`].filter(Boolean);
  return (
    <>
      issued a <b>{d.kind}</b> token to <b>{d.sub}</b>
      <span className="text-faint">
        {scope.join(" · ")}
        {d.exp ? ` · expires ${new Date(d.exp).toLocaleString()}` : ""}
      </span>
    </>
  );
}
